import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { toast } from "sonner";
import { Upload, BarChart3, TrendingUp, Download, Factory, RefreshCw, FileDown, Package, X, Search, Filter } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";
import { fmtDate } from "@/lib/dateFormat";

export default function DemandPlanningPage() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const perms = user?.permissions || [];
  const canView = roles.some(r => ["Admin","admin","Sales Manager","Finance Manager"].includes(r)) || perms.includes("demand_planning.view");
  const canUpload = roles.some(r => ["Admin","admin","Sales Manager"].includes(r)) || perms.includes("demand_planning.upload_projection");
  const canViewProdPlan = roles.some(r => ["Admin","admin","Warehouse Manager"].includes(r)) || perms.includes("demand_planning.view");

  const [tab, setTab] = useState(canUpload ? "upload" : "compare");
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [projections, setProjections] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [dispatched, setDispatched] = useState(null);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prodPlan, setProdPlan] = useState(null);
  const [poFulfilment, setPoFulfilment] = useState(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillTab, setDrillTab] = useState("");
  const [drillTitle, setDrillTitle] = useState("");
  const [drillLoading, setDrillLoading] = useState(false);

  // Filters & Search
  const [searchText, setSearchText] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterSku, setFilterSku] = useState("");
  const [filterProdOnly, setFilterProdOnly] = useState(false);
  const [filterGapSeverity, setFilterGapSeverity] = useState(""); // "red","amber","green"
  const [filterPctMax, setFilterPctMax] = useState(""); // for dispatched % done filter

  const activeFilterCount = [searchText, filterChannel, filterSku, filterProdOnly, filterGapSeverity, filterPctMax].filter(Boolean).length;
  const clearFilters = () => { setSearchText(""); setFilterChannel(""); setFilterSku(""); setFilterProdOnly(false); setFilterGapSeverity(""); setFilterPctMax(""); };

  // Extract unique channels and SKUs from current tab data for dropdowns
  const getOptions = () => {
    let rows = [];
    if (tab === "list") rows = projections || [];
    else if (tab === "compare") rows = comparison?.rows || [];
    else if (tab === "dispatched") rows = dispatched?.rows || [];
    else if (tab === "prodplan") rows = prodPlan?.rows || [];
    else if (tab === "pofulfilment") rows = poFulfilment?.rows || [];
    const channels = [...new Set(rows.map(r => r.channel_name).filter(Boolean))].sort();
    const skus = [...new Set(rows.map(r => r.sku_name || r.sku_id).filter(Boolean))].sort();
    return { channels, skus };
  };

  // Client-side filter function
  const applyFilters = (rows) => {
    if (!rows) return [];
    let filtered = [...rows];
    if (searchText) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(r => (r.channel_name || "").toLowerCase().includes(q) || (r.sku_name || "").toLowerCase().includes(q) || (r.sku_id || "").toLowerCase().includes(q));
    }
    if (filterChannel) filtered = filtered.filter(r => (r.channel_name || "") === filterChannel);
    if (filterSku) filtered = filtered.filter(r => (r.sku_name || r.sku_id || "") === filterSku);
    // Tab-specific
    if (tab === "prodplan" && filterProdOnly) filtered = filtered.filter(r => r.produce_procure > 0 || r.stock_not_entered);
    if (tab === "dispatched" && filterPctMax) filtered = filtered.filter(r => (r.pct_achieved || 0) <= parseFloat(filterPctMax));
    if (tab === "pofulfilment" && filterGapSeverity) {
      const check = (v) => filterGapSeverity === "red" ? v !== null && v < 50 : filterGapSeverity === "amber" ? v !== null && v >= 50 && v < 80 : filterGapSeverity === "green" ? v !== null && v >= 80 : true;
      filtered = filtered.filter(r => check(r.proj_to_ordered_pct) || check(r.ordered_to_dispatched_pct));
    }
    return filtered;
  };

  const TAB_EXPORT_MAP = { list: "projections", compare: "target-vs-projection", dispatched: "dispatched", prodplan: "production-plan", pofulfilment: "po-fulfilment" };
  const exportTab = async (tabKey) => {
    const mapped = TAB_EXPORT_MAP[tabKey] || tabKey;
    try {
      const resp = await api.get("/demand-planning/export-tab", { params: { tab: mapped, period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = `DP_${tabKey}_${period}.xlsx`; a.click();
      toast.success("Export downloaded");
    } catch(e) { toast.error("Export failed"); }
  };

  const fN = (n) => n != null ? n.toLocaleString("en-IN") : "—";
  const fR = (v) => v != null ? `₹${v.toLocaleString("en-IN", {maximumFractionDigits: 0})}` : "—";

  const loadProjections = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demand-planning/projections", { params: { period, include_superseded: showSuperseded ? "true" : "false" } });
      setProjections(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const loadComparison = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demand-planning/target-vs-projection", { params: { period } });
      setComparison(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const loadDispatched = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demand-planning/dispatched-vs-projection", { params: { period } });
      setDispatched(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const loadProdPlan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demand-planning/produce-procure", { params: { period } });
      setProdPlan(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const loadPoFulfilment = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demand-planning/po-fulfilment", { params: { period } });
      setPoFulfilment(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const openDrill = async (tabKey, params, title) => {
    setDrillLoading(true); setDrillOpen(true); setDrillTitle(title); setDrillTab(tabKey); setDrillData(null);
    try {
      const { data } = await api.get(`/demand-planning/drill/${tabKey}`, { params: { ...params, period } });
      setDrillData(data);
    } catch(e) { console.error(e); toast.error("Failed to load details"); }
    setDrillLoading(false);
  };

  const exportDrill = async (tabKey, params) => {
    try {
      const resp = await api.get("/demand-planning/drill/export", { params: { tab: tabKey, ...params, period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = `DP_${tabKey}_${period}.xlsx`; a.click();
      toast.success("Export downloaded");
    } catch(e) { toast.error("Export failed"); }
  };

  useEffect(() => {
    if (tab === "list") loadProjections();
    else if (tab === "compare") loadComparison();
    else if (tab === "dispatched") loadDispatched();
    else if (tab === "prodplan") loadProdPlan();
    else if (tab === "pofulfilment") loadPoFulfilment();
  }, [tab, period, showSuperseded]);

  const [pendingCsvText, setPendingCsvText] = useState("");
  const [uploadErrors, setUploadErrors] = useState(null);

  const handleUpload = async (confirmDecreases = false) => {
    if (!file && !pendingCsvText) { toast.error("Select a CSV file"); return; }
    setUploading(true); if (!confirmDecreases) { setUploadResult(null); setUploadErrors(null); }
    try {
      if (confirmDecreases && pendingCsvText) {
        // Re-confirm flow uses JSON (small payload, just the text we already have)
        const { data } = await api.post("/demand-planning/projections/upload", { csv_text: pendingCsvText, confirm_decreases: true }, { timeout: 120000 });
        setUploadResult(data);
        if (data.status === "success") toast.success(`${data.inserted} projections uploaded`);
        else if (data.status === "rejected") toast.error(`${data.error_count} error(s) found — file rejected`);
        else if (data.status === "confirmation_required") toast.info("Quantity decreases detected — confirm to proceed");
      } else {
        // Primary upload uses multipart FormData (bypasses WAF body inspection)
        const text = await file.text();
        setPendingCsvText(text);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("confirm_decreases", "false");
        const { data } = await api.post("/demand-planning/projections/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        setUploadResult(data);
        if (data.status === "success") toast.success(`${data.inserted} projections uploaded`);
        else if (data.status === "rejected") toast.error(`${data.error_count} error(s) found — file rejected`);
        else if (data.status === "confirmation_required") toast.info("Quantity decreases detected — confirm to proceed");
      }
    } catch(e) {
      console.error("[DemandPlanning Upload] Error:", e.message, "Status:", e.response?.status, "Data:", e.response?.data);
      const status = e.response?.status;
      const respData = e.response?.data;
      if (respData?.detail) {
        const d = respData.detail;
        const items = Array.isArray(d) ? d.map(x => typeof x === "string" ? x : (x.msg || JSON.stringify(x))) : [String(d)];
        setUploadErrors(items);
        toast.error(`Upload rejected — ${items.length} issue(s). See details below.`);
      } else if (status === 502 || status === 504) {
        setUploadErrors(["The server took too long to respond (gateway timeout). This usually means the production database is slow. Please try again in a minute."]);
        toast.error("Server timeout — see details below");
      } else if (status) {
        const msg = typeof respData === "string" ? respData.slice(0, 200) : JSON.stringify(respData || {}).slice(0, 200);
        setUploadErrors([`Server returned HTTP ${status}: ${msg}`]);
        toast.error(`Upload failed — HTTP ${status}`);
      } else if (e.code === "ERR_NETWORK" || e.code === "ECONNABORTED" || !e.response) {
        setUploadErrors(["Network error: The request did not reach the server, or the server did not respond in time. Check your internet connection and try again. If this persists, the server may be overloaded."]);
        toast.error("Network error — see details below");
      } else {
        setUploadErrors([`Unexpected error: ${e.message}`]);
        toast.error("Upload failed — see details below");
      }
    }
    setUploading(false);
  };

  const TABS = [
    ...(canUpload ? [{ key: "upload", label: "Upload Projections", icon: Upload }] : []),
    { key: "list", label: "Projection History", icon: BarChart3 },
    { key: "compare", label: "Target vs Projection", icon: TrendingUp },
    { key: "dispatched", label: "Dispatched Tracking", icon: TrendingUp },
    ...(canViewProdPlan ? [{ key: "prodplan", label: "Production Plan", icon: Factory }] : []),
    { key: "pofulfilment", label: "PO Fulfilment", icon: Package },
  ];

  return (
    <div className="space-y-4" data-testid="demand-planning-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-zinc-900">Demand Planning</h1>
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-zinc-500 uppercase font-semibold">Period</label>
          <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="h-8 text-xs w-36" />
        </div>
      </div>
      <div className="flex gap-1 border-b border-zinc-200 pb-2">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-t-sm transition-colors ${tab === t.key ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"}`} data-testid={`dp-tab-${t.key}`}>
            <t.icon className="w-3 h-3" />{t.label}
          </button>
        ))}
      </div>

      {/* Filter & Export Bar — visible on all data tabs */}
      {tab !== "upload" && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search channel, SKU..." className="h-8 text-xs rounded-sm pl-8" data-testid="dp-search" />
          </div>
          <Select value={filterChannel || "all"} onValueChange={v => setFilterChannel(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8 text-xs rounded-sm"><SelectValue placeholder="All Channels" /></SelectTrigger>
            <SelectContent>{[{v:"all",l:"All Channels"}, ...getOptions().channels.map(c => ({v:c,l:c}))].map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterSku || "all"} onValueChange={v => setFilterSku(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-8 text-xs rounded-sm"><SelectValue placeholder="All SKUs" /></SelectTrigger>
            <SelectContent>{[{v:"all",l:"All SKUs"}, ...getOptions().skus.map(s => ({v:s,l:s.length > 30 ? s.slice(0,30)+"…" : s}))].map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {tab === "dispatched" && <Input type="number" value={filterPctMax} onChange={e => setFilterPctMax(e.target.value)} placeholder="Max % done" className="w-28 h-8 text-xs rounded-sm" />}
          {tab === "prodplan" && <label className="text-[10px] flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={filterProdOnly} onChange={e => setFilterProdOnly(e.target.checked)} />Needs production only</label>}
          {tab === "pofulfilment" && <Select value={filterGapSeverity || "all"} onValueChange={v => setFilterGapSeverity(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 h-8 text-xs rounded-sm"><SelectValue placeholder="Gap severity" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Gaps</SelectItem><SelectItem value="red">Red (&lt;50%)</SelectItem><SelectItem value="amber">Amber (50-79%)</SelectItem><SelectItem value="green">Green (≥80%)</SelectItem></SelectContent>
          </Select>}
          {activeFilterCount > 0 && <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500" onClick={clearFilters} data-testid="dp-clear-filters"><X className="w-3 h-3 mr-1" />Clear ({activeFilterCount})</Button>}
          <div className="ml-auto">
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={() => exportTab(tab)} data-testid="dp-export-tab"><FileDown className="w-3.5 h-3.5 mr-1" />Export to Excel</Button>
          </div>
        </div>
      )}

      {/* Upload Tab */}
      {tab === "upload" && canUpload && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={async () => {
              try {
                const r = await api.get("/demand-planning/projections/template", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = url; a.download = "Projection_Template.csv"; a.click();
              } catch(e) { toast.error("Download failed"); }
            }}><Download className="w-3 h-3 mr-1" />Download Template</Button>
          </div>
          <div className="flex gap-2 items-center">
            <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0])} className="text-xs" />
            <Button onClick={handleUpload} disabled={uploading || !file} className="rounded-sm text-xs">{uploading ? "Uploading..." : "Upload Projections"}</Button>
          </div>
          {uploadErrors && (
            <div className="p-3 bg-red-50 border border-red-300 rounded-sm text-xs max-h-56 overflow-y-auto" data-testid="upload-http-errors">
              <p className="font-semibold text-red-700 mb-2">Server rejected the upload — {uploadErrors.length} issue(s):</p>
              {uploadErrors.map((e, i) => <div key={i} className="text-[11px] text-red-600 py-0.5 border-b border-red-100 last:border-0">{e}</div>)}
            </div>
          )}
          {uploadResult && uploadResult.status === "success" && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-xs" data-testid="upload-result">
              <p><strong>{uploadResult.inserted}</strong> projections uploaded, <strong>{uploadResult.superseded}</strong> superseded</p>
              {uploadResult.results?.map((r, i) => <div key={i} className="text-[10px] text-green-600">✅ Row {r.row}: {r.channel} / {r.sku} / {r.period}: {r.qty} units (₹{r.selling_price} × {r.qty} = ₹{r.value})</div>)}
            </div>
          )}
          {uploadResult && uploadResult.status === "rejected" && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-xs" data-testid="upload-errors">
              <p className="font-semibold text-red-700 mb-2">Upload rejected — {uploadResult.error_count} error(s) found. No data was written.</p>
              {uploadResult.errors?.map((e, i) => <div key={i} className="text-[10px] text-red-600 py-0.5">{e}</div>)}
            </div>
          )}
          {uploadResult && uploadResult.status === "confirmation_required" && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs" data-testid="upload-confirm">
              <p className="font-semibold text-amber-700 mb-2">{uploadResult.decreases?.length} row(s) have a LOWER projected quantity. Nothing has been written yet.</p>
              <table className="data-table w-full mb-3"><thead><tr><th>Row</th><th>Channel</th><th>SKU</th><th>Period</th><th className="text-right">Current Qty</th><th className="text-right">New Qty</th></tr></thead><tbody>
                {uploadResult.decreases?.map((d, i) => <tr key={i}><td className="text-xs">{d.row}</td><td className="text-xs">{d.channel}</td><td className="text-xs">{d.sku}</td><td className="text-xs font-mono">{d.period}</td><td className="text-right font-mono text-xs">{fN(d.current_qty)}</td><td className="text-right font-mono text-xs text-red-600 font-semibold">{fN(d.new_qty)}</td></tr>)}
              </tbody></table>
              <div className="flex gap-2">
                <Button onClick={() => handleUpload(true)} className="rounded-sm text-xs bg-amber-600 hover:bg-amber-700">Yes, these decreases are correct — proceed</Button>
                <Button variant="outline" onClick={() => setUploadResult(null)} className="rounded-sm text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Projection List */}
      {tab === "list" && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-[10px]"><input type="checkbox" checked={showSuperseded} onChange={e => setShowSuperseded(e.target.checked)} className="mr-1" />Show superseded</label>
          </div>
          {loading ? <p className="text-xs text-zinc-400">Loading...</p> : (
            <div className="border border-zinc-200 overflow-x-auto"><table className="data-table w-full"><thead><tr><th>Channel</th><th>SKU</th><th>Period</th><th className="text-right">Qty</th><th className="text-right">Value</th><th>Uploaded</th><th></th></tr></thead><tbody>
              {applyFilters(projections).length === 0 ? <tr><td colSpan={8} className="text-center py-4 text-zinc-400 text-xs">No projections for this period</td></tr> : applyFilters(projections).map((p, i) => (
                <tr key={i} className={`cursor-pointer hover:bg-zinc-50 ${p.is_superseded ? "opacity-40 line-through" : ""}`} onClick={() => openDrill("projection-history", {channel_id: p.channel_id, sku_id: p.sku_id}, `${p.channel_name} / ${p.sku_name} / ${p.period}`)}>
                  <td className="text-xs">{p.channel_name}</td><td className="text-xs">{p.sku_name}</td><td className="text-xs font-mono">{p.period}</td>
                  <td className="text-right font-mono text-xs">{fN(p.projected_qty)}</td><td className="text-right font-mono text-xs">{fR(p.projected_value)}</td>
                  <td className="text-[10px] text-zinc-400">{p.uploaded_by_name}</td>
                  <td>{p.is_superseded && <span className="px-1 py-0.5 text-[8px] bg-zinc-200 text-zinc-500 rounded">SUPERSEDED</span>}</td>
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* Target vs Projection */}
      {tab === "compare" && (
        <div>
          {loading ? <p className="text-xs text-zinc-400">Loading...</p> : comparison && (
            <div className="border border-zinc-200 overflow-x-auto"><table className="data-table w-full"><thead><tr><th>Channel</th><th>SKU</th><th className="text-right">Target Qty</th><th className="text-right">Target Value</th><th className="text-right">Projected Qty</th><th className="text-right">Projected Value</th><th className="text-right">Qty Diff</th><th className="text-right">Value Diff</th></tr></thead><tbody>
              {applyFilters(comparison.rows).length === 0 ? <tr><td colSpan={8} className="text-center py-4 text-zinc-400 text-xs">No data for this period</td></tr> : applyFilters(comparison.rows).map((r, i) => (
                <tr key={i} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDrill("target-vs-projection", {channel_id: r.channel_id, sku_id: r.sku_id}, `${r.channel_name} / ${r.sku_name}`)}>
                  <td className="text-xs">{r.channel_name}</td><td className="text-xs">{r.sku_name}</td>
                  <td className="text-right font-mono text-xs">{r.target_qty != null ? fN(r.target_qty) : <span className="text-zinc-300">—</span>}</td>
                  <td className="text-right font-mono text-xs">{r.target_value != null ? fR(r.target_value) : <span className="text-zinc-300">—</span>}</td>
                  <td className="text-right font-mono text-xs">{r.projected_qty != null ? fN(r.projected_qty) : <span className="text-zinc-300">—</span>}</td>
                  <td className="text-right font-mono text-xs">{r.projected_value != null ? fR(r.projected_value) : <span className="text-zinc-300">—</span>}</td>
                  <td className={`text-right font-mono text-xs font-semibold ${r.qty_difference > 0 ? "text-green-600" : r.qty_difference < 0 ? "text-red-600" : ""}`}>{r.qty_difference != null ? (r.qty_difference > 0 ? "+" : "") + fN(r.qty_difference) : "—"}</td>
                  <td className={`text-right font-mono text-xs ${r.value_difference > 0 ? "text-green-600" : r.value_difference < 0 ? "text-red-600" : ""}`}>{r.value_difference != null ? (r.value_difference > 0 ? "+" : "") + fR(r.value_difference) : "—"}</td>
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* Dispatched Tracking */}
      {tab === "dispatched" && (
        <div>
          <p className="text-[10px] text-zinc-400 mb-2">Quantities calculated by order date for the selected calendar month.</p>
          {loading ? <p className="text-xs text-zinc-400">Loading...</p> : dispatched && (
            <div className="border border-zinc-200 overflow-x-auto"><table className="data-table w-full"><thead><tr><th>Channel</th><th>SKU</th><th className="text-right">Projected</th><th className="text-right">Dispatched</th><th className="text-right">% Done</th><th className="text-right">Qty Due</th>{dispatched.rows?.some(r => r.confirmed_received_qty != null) && <th className="text-right">Confirmed Received</th>}</tr></thead><tbody>
              {applyFilters(dispatched.rows).length === 0 ? <tr><td colSpan={7} className="text-center py-4 text-zinc-400 text-xs">No projections for this period</td></tr> : applyFilters(dispatched.rows).map((r, i) => (
                <tr key={i} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDrill("dispatched", {channel_id: r.channel_id, sku_id: r.sku_id}, `${r.channel_name} / ${r.sku_name}`)}>
                  <td className="text-xs">{r.channel_name}</td><td className="text-xs">{r.sku_name}</td>
                  <td className="text-right font-mono text-xs">{fN(r.projected_qty)}</td>
                  <td className="text-right font-mono text-xs">{fN(r.dispatched_qty)}</td>
                  <td className={`text-right font-mono text-xs font-semibold ${r.pct_achieved >= 100 ? "text-green-600" : r.pct_achieved >= 50 ? "text-amber-600" : "text-red-600"}`}>{r.pct_achieved}%</td>
                  <td className="text-right font-mono text-xs">{fN(r.qty_due)}</td>
                  {r.confirmed_received_qty != null && <td className="text-right font-mono text-xs text-blue-600">{fN(r.confirmed_received_qty)}<span className="text-[9px] text-zinc-400 ml-1">(platform)</span></td>}
                  {r.customer_match_failed && <td className="text-[9px] text-amber-600">SS match failed</td>}
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* Production Plan */}
      {tab === "prodplan" && canViewProdPlan && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-zinc-500">Company-wide production/procurement needs for {period}</p>
            <p className="text-[10px] text-zinc-400">Quantities calculated by order date for the selected calendar month. Stock at Skippi defaults to 0 until the OPS stock module is in use.</p>
            <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm" onClick={loadProdPlan} data-testid="refresh-prod-plan"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
          </div>
          {loading ? <p className="text-xs text-zinc-400">Loading...</p> : prodPlan && (
            <>
              <div className="border border-zinc-200 overflow-x-auto"><table className="data-table w-full"><thead><tr><th>SKU Name</th><th>SKU Code</th><th className="text-right">Projected</th><th className="text-right">Dispatched</th><th className="text-right">Picked</th><th className="text-right">Stock at Skippi</th><th className="text-right">Still to Produce/Procure</th></tr></thead><tbody>
                {applyFilters(prodPlan.rows).length === 0 ? <tr><td colSpan={7} className="text-center py-4 text-zinc-400 text-xs">No projections for this period</td></tr> : applyFilters(prodPlan.rows).map((r, i) => {
                  const pctRemaining = r.projected_qty > 0 && r.produce_procure != null ? (r.produce_procure / r.projected_qty) * 100 : 0;
                  const ppColor = r.produce_procure === 0 ? "text-green-600" : pctRemaining > 50 ? "text-red-600" : pctRemaining > 20 ? "text-amber-600" : "text-green-600";
                  return (
                    <tr key={i} data-testid={`pp-row-${r.sku_id}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDrill("production-plan", {sku_id: r.sku_id}, `${r.sku_name} / ${period}`)}>
                      <td className="text-xs font-medium">{r.sku_name}</td>
                      <td className="text-xs font-mono text-zinc-500">{r.sku_id}</td>
                      <td className="text-right font-mono text-xs">{fN(r.projected_qty)}</td>
                      <td className="text-right font-mono text-xs">{fN(r.dispatched_qty)}</td>
                      <td className="text-right font-mono text-xs">{fN(r.picked_qty)}</td>
                      <td className="text-right text-xs">{r.stock_not_entered ? <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-700 rounded-sm">Not yet entered</span> : <span className="font-mono">{fN(r.stock_at_skippi)}</span>}</td>
                      <td className={`text-right text-xs font-semibold ${ppColor}`}>{r.produce_procure === 0 ? "✓ Fulfilled" : fN(r.produce_procure)}</td>
                    </tr>
                  );
                })}
              </tbody></table></div>
              {prodPlan.fetched_at && <p className="text-[9px] text-zinc-400 mt-2">Last updated: {fmtDateTime(prodPlan.fetched_at)}</p>}
            </>
          )}
        </div>
      )}

      {/* PO Fulfilment */}
      {tab === "pofulfilment" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] text-zinc-500">Order fulfilment pipeline for {period}</p>
              <p className="text-[10px] text-zinc-400">Quantities calculated by order date for the selected calendar month.</p>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm" onClick={loadPoFulfilment}><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
          </div>
          {loading ? <p className="text-xs text-zinc-400">Loading...</p> : poFulfilment && (
            <>
              <div className="border border-zinc-200 overflow-x-auto"><table className="data-table w-full"><thead><tr><th>Channel</th><th>SKU</th><th className="text-right">Projected</th><th className="text-right">Ordered</th><th className="text-right">Dispatched</th><th className="text-right">Proj→Ordered</th><th className="text-right">Ordered→Disp</th></tr></thead><tbody>
                {applyFilters(poFulfilment.rows).length === 0 ? <tr><td colSpan={7} className="text-center py-4 text-zinc-400 text-xs">No projections for this period</td></tr> : applyFilters(poFulfilment.rows).map((r, i) => (
                  <tr key={i} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDrill("po-fulfilment", {channel_id: r.channel_id, sku_id: r.sku_id}, `${r.channel_name} / ${r.sku_name}`)}>
                    <td className="text-xs">{r.channel_name}</td>
                    <td className="text-xs">{r.sku_name}</td>
                    <td className="text-right font-mono text-xs">{fN(r.projected_qty)}</td>
                    <td className="text-right font-mono text-xs">{fN(r.ordered_qty)}</td>
                    <td className="text-right font-mono text-xs">{fN(r.dispatched_qty)}</td>
                    <td className="text-right text-xs"><GapBadge v={r.proj_to_ordered_pct} /></td>
                    <td className="text-right text-xs"><GapBadge v={r.ordered_to_dispatched_pct} /></td>
                  </tr>
                ))}
              </tbody></table></div>
              {poFulfilment.fetched_at && <p className="text-[9px] text-zinc-400 mt-2">Last updated: {fmtDateTime(poFulfilment.fetched_at)}</p>}
            </>
          )}
        </div>
      )}

      {/* ═══ DRILL-DOWN SHEET ═══ */}
      <Sheet open={drillOpen} onOpenChange={setDrillOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[40vw] overflow-y-auto" data-testid="drill-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading text-sm">{drillTitle}</SheetTitle>
            <SheetDescription className="text-[10px]">{drillTab.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())} — {period}</SheetDescription>
          </SheetHeader>
          {drillLoading ? <p className="text-xs text-zinc-400 mt-8 text-center">Loading...</p> : drillData && (
            <div className="space-y-4 mt-4">
              <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm" onClick={() => exportDrill(drillTab, drillTab === "production-plan" ? {sku_id: drillData.sku_id} : {channel_id: drillData.channel_id, sku_id: drillData.sku_id})} data-testid="drill-export-btn"><FileDown className="w-3 h-3 mr-1" />Export to Excel</Button>

              {/* Projection History drill */}
              {drillTab === "projection-history" && drillData.rows && (
                <div className="border border-zinc-200 rounded-sm overflow-hidden"><table className="w-full text-xs"><thead><tr className="bg-zinc-50"><th className="px-2 py-1 text-left text-[10px]">#</th><th className="px-2 py-1 text-left text-[10px]">Uploaded By</th><th className="px-2 py-1 text-left text-[10px]">Uploaded At</th><th className="px-2 py-1 text-right text-[10px]">Qty</th><th className="px-2 py-1 text-right text-[10px]">SP</th><th className="px-2 py-1 text-right text-[10px]">Value</th><th className="px-2 py-1 text-left text-[10px]">Status</th></tr></thead><tbody>
                  {drillData.rows.map((r, i) => (
                    <tr key={i} className={`border-t border-zinc-100 ${r.is_superseded ? "opacity-40" : ""}`}>
                      <td className="px-2 py-1">{i+1}</td><td className="px-2 py-1">{r.uploaded_by_name}</td><td className="px-2 py-1 text-zinc-500">{fmtDateTime(r.uploaded_at)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fN(r.projected_qty)}</td><td className="px-2 py-1 text-right font-mono">₹{r.selling_price}</td><td className="px-2 py-1 text-right font-mono">{fR(r.total_value)}</td>
                      <td className="px-2 py-1">{r.is_superseded ? <span className="px-1 py-0.5 text-[8px] bg-zinc-200 text-zinc-500 rounded">Superseded</span> : <span className="px-1 py-0.5 text-[8px] bg-green-100 text-green-700 rounded">Current</span>}</td>
                    </tr>
                  ))}
                </tbody></table></div>
              )}

              {/* Target vs Projection drill */}
              {drillTab === "target-vs-projection" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                    <p className="text-[10px] font-semibold text-blue-700 mb-2">Target</p>
                    {drillData.target ? <>
                      <p className="text-lg font-bold">{fN(drillData.target.qty)}</p>
                      <p className="text-xs text-zinc-500">Value: {fR(drillData.target.value)}</p>
                      <p className="text-[10px] text-zinc-400 mt-1">Set by {drillData.target.set_by}</p>
                    </> : <p className="text-xs text-zinc-400">No target set</p>}
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                    <p className="text-[10px] font-semibold text-green-700 mb-2">Projection</p>
                    {drillData.projection ? <>
                      <p className="text-lg font-bold">{fN(drillData.projection.qty)}</p>
                      <p className="text-xs text-zinc-500">Value: {fR(drillData.projection.total_value)}</p>
                      <p className="text-[10px] text-zinc-400 mt-1">By {drillData.projection.uploaded_by}</p>
                    </> : <p className="text-xs text-zinc-400">No projection</p>}
                  </div>
                  {drillData.gap_qty != null && <div className="col-span-2 p-2 bg-zinc-50 rounded-sm text-center"><span className="text-xs font-semibold">Gap: {drillData.gap_qty > 0 ? "+" : ""}{fN(drillData.gap_qty)} ({drillData.gap_pct}%)</span></div>}
                </div>
              )}

              {/* Dispatched drill */}
              {drillTab === "dispatched" && drillData.rows && (
                <div className="border border-zinc-200 rounded-sm overflow-hidden"><table className="w-full text-xs"><thead><tr className="bg-zinc-50"><th className="px-2 py-1 text-left text-[10px]">Order #</th><th className="px-2 py-1 text-left text-[10px]">Customer</th><th className="px-2 py-1 text-left text-[10px]">Invoice Date</th><th className="px-2 py-1 text-right text-[10px]">Qty</th><th className="px-2 py-1 text-left text-[10px]">Status</th><th className="px-2 py-1 text-left text-[10px]">PO #</th></tr></thead><tbody>
                  {drillData.rows.length === 0 ? <tr><td colSpan={6} className="text-center py-4 text-zinc-400">No dispatched orders</td></tr> : drillData.rows.map((r, i) => (
                    <tr key={i} className="border-t border-zinc-100"><td className="px-2 py-1 font-mono">{r.order_number}</td><td className="px-2 py-1">{r.customer_name}</td><td className="px-2 py-1 text-zinc-500">{fmtDate(r.invoice_date)}</td><td className="px-2 py-1 text-right font-mono">{fN(r.sku_qty)}</td><td className="px-2 py-1">{r.order_status}</td><td className="px-2 py-1 text-zinc-400">{r.po_number || "—"}</td></tr>
                  ))}
                </tbody></table></div>
              )}

              {/* Production Plan drill */}
              {drillTab === "production-plan" && (
                <div className="space-y-4">
                  <DrillSection title={`Projections by Channel (${drillData.projections_by_channel?.length || 0})`}>
                    {drillData.projections_by_channel?.map((r, i) => <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-50"><span>{r.channel_name}</span><span className="font-mono">{fN(r.projected_qty)}</span></div>)}
                  </DrillSection>
                  <DrillSection title={`Dispatched Orders (${drillData.dispatched_orders?.length || 0})`}>
                    {drillData.dispatched_orders?.length === 0 ? <p className="text-xs text-zinc-400">None</p> : drillData.dispatched_orders?.map((r, i) => <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-50"><span>{r.order_number} — {r.customer_name}</span><span className="font-mono">{fN(r.sku_qty)}</span></div>)}
                  </DrillSection>
                  <DrillSection title={`Picked Orders (${drillData.picked_orders?.length || 0})`}>
                    {drillData.picked_orders?.length === 0 ? <p className="text-xs text-zinc-400">None</p> : drillData.picked_orders?.map((r, i) => <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-50"><span>{r.order_number} ({r.order_status})</span><span className="font-mono">{fN(r.picked_qty)}</span></div>)}
                  </DrillSection>
                  <DrillSection title="Stock at Skippi">
                    {drillData.stock_at_skippi ? <div className="text-xs"><span className="font-mono font-semibold">{fN(drillData.stock_at_skippi.qty)}</span> units<span className="text-[9px] text-zinc-400 ml-2">Source: {drillData.stock_at_skippi.source} | Updated: {(drillData.stock_at_skippi.last_updated || "").slice(0, 10)}</span></div> : <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-700 rounded-sm">Not yet entered</span>}
                  </DrillSection>
                </div>
              )}

              {/* PO Fulfilment drill */}
              {drillTab === "po-fulfilment" && (
                <div className="space-y-4">
                  <DrillSection title={`ORBIT Orders (${drillData.orbit_orders?.length || 0})`}>
                    {drillData.orbit_orders?.length === 0 ? <p className="text-xs text-zinc-400">None</p> : <div className="border border-zinc-200 rounded-sm overflow-hidden"><table className="w-full text-xs"><thead><tr className="bg-zinc-50"><th className="px-2 py-1 text-left text-[10px]">Order #</th><th className="px-2 py-1 text-left text-[10px]">Date</th><th className="px-2 py-1 text-right text-[10px]">Qty</th><th className="px-2 py-1 text-left text-[10px]">Status</th></tr></thead><tbody>
                      {drillData.orbit_orders.map((r, i) => <tr key={i} className="border-t border-zinc-100"><td className="px-2 py-1 font-mono">{r.order_number}</td><td className="px-2 py-1 text-zinc-500">{fmtDate(r.order_date)}</td><td className="px-2 py-1 text-right font-mono">{fN(r.sku_qty)}</td><td className="px-2 py-1">{r.status}</td></tr>)}
                    </tbody></table></div>}
                  </DrillSection>
                  <DrillSection title={`Dispatched (${drillData.dispatched_orders?.length || 0})`}>
                    {drillData.dispatched_orders?.length === 0 ? <p className="text-xs text-zinc-400">None</p> : drillData.dispatched_orders.map((r, i) => <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-50"><span>{r.order_number} — {r.customer_name}</span><span className="font-mono">{fN(r.sku_qty)}</span></div>)}
                  </DrillSection>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function GapBadge({ v }) {
  if (v == null) return <span className="text-zinc-400 text-[10px]">—</span>;
  const color = v >= 80 ? "bg-green-100 text-green-700" : v >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${color}`}>{v}%</span>;
}

function DrillSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[10px] font-semibold uppercase text-zinc-500 mb-1 hover:text-zinc-700">
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>▶</span>{title}
      </button>
      {open && <div className="ml-3">{children}</div>}
    </div>
  );
}
